/**
 * Reservation-flow popup that displays the host's three legal documents
 * (Terms & Conditions, Cancellation & Refund, Privacy Policy) inline so
 * renters no longer have to leave the booking widget to read them.
 *
 * Data source: GET /api/vendors/:vendor_id/policies — returns each policy
 * with {content, is_default}. The popup is opened from the
 * "I have read and accept the host's ___" row in BookingWidget.jsx. The
 * `initialSection` prop pre-selects whichever item the user clicked.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, ShieldOff, Lock, Loader2, BadgeAlert } from "lucide-react";

const POLICY_META = [
  { key: "terms_and_conditions", label: "Terms",              icon: FileText,  tone: "text-blue-700",   bg: "bg-blue-50" },
  { key: "cancellation_policy",  label: "Cancellation policy",icon: ShieldOff, tone: "text-amber-700",  bg: "bg-amber-50" },
  { key: "privacy_policy",       label: "Privacy policy",     icon: Lock,      tone: "text-emerald-700",bg: "bg-emerald-50" },
];

export default function BookingPoliciesPopup({ open, onOpenChange, vendorId, initialSection = "terms_and_conditions" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(initialSection);

  useEffect(() => { if (open) setTab(initialSection); }, [open, initialSection]);

  useEffect(() => {
    if (!open || !vendorId || data) return;
    api.get(`/vendors/${vendorId}/policies`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load policies."));
  }, [open, vendorId, data]);

  const vendorName = data?.vendor_name;
  const policies = useMemo(() => data?.policies || {}, [data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[88vh] overflow-hidden p-0 gap-0"
        data-testid="booking-policies-popup"
      >
        <DialogHeader className="px-5 py-4 border-b bg-gradient-to-br from-blue-50/40 via-white to-emerald-50/40">
          <DialogTitle className="flex items-center gap-2 text-lg font-display">
            <span className="h-9 w-9 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-200">
              <FileText className="h-5 w-5" />
            </span>
            <span>Host's policies{vendorName ? <span className="ml-2 text-sm text-neutral-500 font-normal">· {vendorName}</span> : null}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Read the host's Terms, Cancellation policy and Privacy policy before reserving.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(88vh-72px)] px-5 py-4">
          {err && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-3 text-rose-800 text-sm" data-testid="booking-policies-error">{err}</div>
          )}
          {!err && !data && (
            <div className="flex items-center justify-center py-12 text-neutral-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading host's policies…
            </div>
          )}
          {!err && data && (
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid grid-cols-3 mb-4" data-testid="booking-policies-tablist">
                {POLICY_META.map(({ key, label, icon: Icon }) => (
                  <TabsTrigger key={key} value={key} className="gap-1.5 text-xs sm:text-sm" data-testid={`booking-policies-tab-${key}`}>
                    <Icon className="h-3.5 w-3.5" />
                    <span className="truncate">{label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {POLICY_META.map(({ key, label, icon: Icon, tone, bg }) => {
                const p = policies[key];
                return (
                  <TabsContent key={key} value={key} className="mt-0">
                    <div className="rounded-2xl border border-border bg-white p-4 sm:p-5" data-testid={`booking-policies-panel-${key}`}>
                      <div className="flex items-center gap-2.5 mb-3">
                        <span className={`h-9 w-9 rounded-xl ${bg} flex items-center justify-center ${tone}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <div>
                          <div className="font-semibold text-sm text-neutral-900">{label}</div>
                          {p?.is_default && (
                            <div className="text-[10px] uppercase tracking-wider text-amber-700 mt-0.5 flex items-center gap-1">
                              <BadgeAlert className="h-3 w-3" /> RentalCar.al default — host has not authored their own
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-[13px] text-neutral-700 leading-relaxed whitespace-pre-wrap" data-testid={`booking-policies-content-${key}`}>
                        {p?.content || (
                          <span className="italic text-neutral-400">No content available for this section.</span>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
